import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { Button, CodeInput } from '@yellowcard/components-library-vue3';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import TransactHeader from '@/components/transact/TransactHeader.vue';
import { numberFormat, getChannelType } from '@/utils/helpers';
import useTransactionsStore from '@/store/modules/transactions';
import { storeToRefs } from 'pinia';
import router, { RoutePaths } from '@/router';
import validator from 'validator';
import { useAuthStore } from '@/store/modules/auth';
import { useKycStore } from '@/store/modules/kyc';
import { usePaymentStore } from '@/store/modules/payment';
import { usePayoutAccountsStore } from '@/store/modules/payout-accounts';
import { PaymentTypes } from '@/types/payment';
import { AuthTypes } from '@/types/auth';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import { TransactTypes } from '@/types/transact';
import { KycTypes } from '@/types/kyc';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import setContext from '@/utils/ddrum';
export default defineComponent({
    name: 'OtpInput',
    components: {
        Button,
        CodeInput,
        LoaderComponent,
        TransactHeader,
    },
    setup() {
        const transactionsStore = useTransactionsStore();
        const { loadingMessage, txType, selectedChannel, walletAddress, shouldTriggerTravelRule, } = storeToRefs(transactionsStore);
        const paymentStore = usePaymentStore();
        const { currentStep: paymentCurrentStep } = storeToRefs(paymentStore);
        const authStore = useAuthStore();
        const { email, session, isLoggedIn, currentStep: authCurrentStep, standaloneAuthFlow, user, partnerId, } = storeToRefs(authStore);
        const kycStore = useKycStore();
        const { kycStatus } = storeToRefs(kycStore);
        const payoutAccountsStore = usePayoutAccountsStore();
        const { currentStep: payoutAccountsStep, selectedPayoutMethod, fromTransaction, payoutAccounts, } = storeToRefs(payoutAccountsStore);
        const buttonDisabled = ref(true);
        const loading = ref(false);
        const error = ref(undefined);
        const userInput = reactive({
            otp: '',
        });
        const rules = computed(() => ({
            otp: (value) => {
                if (validator.isEmpty(value))
                    return 'Your OTP is required';
                if (value.length < 6)
                    return false;
                return true;
            },
        }));
        const resetLoading = () => {
            loading.value = false;
            loadingMessage.value = '';
        };
        const submitCode = async () => {
            error.value = undefined;
            buttonDisabled.value = true;
            loading.value = true;
            loadingMessage.value = 'Verifying your email';
            trackEvent(MIXPANEL_EVENTS.SIGNIN.OTP, {
                partner: partnerId.value,
            });
            try {
                await authStore.SubmitCode({
                    email: email.value,
                    session: session.value,
                    code: userInput.otp,
                });
            }
            catch {
                resetLoading();
                error.value = 'Invalid session. Enter the new code sent.';
                await authStore.SignIn({ email: email.value });
                return;
            }
            if (isLoggedIn.value) {
                setContext('userId', user.value?.id);
                setContext('email', user.value?.email);
            }
            if (standaloneAuthFlow.value && isLoggedIn.value) {
                await router.push(RoutePaths.Home);
                standaloneAuthFlow.value = false;
                return;
            }
            if (!isLoggedIn.value) {
                error.value = 'OTP incorrect, please try again';
                resetLoading();
                return;
            }
            if (![KycTypes.KycStatus.APPROVED, KycTypes.KycStatus.INCOMPLETE].includes(kycStatus.value)) {
                await router.push(RoutePaths.Kyc);
                resetLoading();
                return;
            }
            switch (txType.value) {
                case TransactTypes.TransactionType.BUY: {
                    if (walletAddress.value.address && !shouldTriggerTravelRule.value) {
                        // call submit order
                        await transactionsStore.getBankDetails({});
                        return;
                    }
                    paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
                    await router.push(RoutePaths.Payment);
                    break;
                }
                case TransactTypes.TransactionType.SELL: {
                    transactionsStore.setFees();
                    await payoutAccountsStore.getPayoutAccounts();
                    fromTransaction.value = true;
                    selectedPayoutMethod.value = getChannelType(selectedChannel.value);
                    payoutAccountsStep.value = payoutAccounts.value.filter(account => account.accountType === selectedPayoutMethod.value).length
                        ? PayoutAccountsTypes.PayoutComponents.ManagePayouts
                        : PayoutAccountsTypes.PayoutComponents.FormSelect;
                    await router.push(RoutePaths.PayoutAccounts);
                    break;
                }
                default:
                    paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
            }
            resetLoading();
        };
        watch(userInput, newInput => {
            buttonDisabled.value = rules.value.otp(newInput.otp) !== true;
            if (newInput.otp && newInput.otp.length === 6) {
                submitCode();
            }
        });
        const resendCode = async () => {
            error.value = undefined;
            buttonDisabled.value = true;
            loading.value = true;
            loadingMessage.value = 'Sending you a secure OTP via email';
            try {
                await authStore.SignIn({
                    email: email.value,
                });
                resetLoading();
            }
            catch (e) {
                console.log('error while resending auth code');
                resetLoading();
            }
        };
        const onKeyPress = (event) => {
            event.preventDefault();
        };
        const goBack = () => {
            authCurrentStep.value = AuthTypes.AuthSteps.AUTH_1;
        };
        return {
            error,
            numberFormat,
            loading,
            email,
            userInput,
            rules,
            submitCode,
            resendCode,
            onKeyPress,
            buttonDisabled,
            goBack,
            standaloneAuthFlow,
        };
    },
});
